/**
 * Card — V1.1 hairline border + soft shadow.
 *
 * Brand spec §2.5. The V1 ring-soft is retired; do not re-introduce it.
 */
import * as React from "react";
import { cn } from "../lib/cn.js";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Sky rail down the left edge — the V1.1 emphasis pattern (§13.2). */
  rail?: "none" | "sky" | "warn";
  /**
   * Render as a different element (e.g. `"section"`) while keeping the card
   * chrome — so consumers compose the chrome instead of copying its classes.
   */
  as?: keyof React.JSX.IntrinsicElements;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, rail = "none", as = "div", ...props },
  ref,
) {
  // Narrowed to "div" so the HTMLDivElement ref/props contract stays put —
  // the chrome is tag-agnostic and the runtime element is whatever `as` names.
  const Comp = as as "div";
  return (
    <Comp
      ref={ref}
      className={cn(
        "bg-paper border border-line rounded shadow",
        rail === "sky" && "shadow-rail",
        rail === "warn" && "shadow-rail-warn",
        className,
      )}
      {...props}
    />
  );
});

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return <div ref={ref} className={cn("p-4 border-b border-line", className)} {...props} />;
  },
);

export const CardBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardBody({ className, ...props }, ref) {
    return <div ref={ref} className={cn("p-4", className)} {...props} />;
  },
);
